import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete, Skeleton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Pagelayout from "../../components/Pagelayout";
import {
  addPayHeadApi,
  payHeadInitialApi,
  singlePayHeadApi,
  updatePayHeadApi,
} from "../../parollAPIs";
import { ErrorDisplay } from "../settings/PayrollSettings";
import { calculationTypeOption } from "../../payrollEnums";

const CreatePayHead = () => {
  const [calculationTypeOptions, setCalculationTypeOptions] = useState(
    calculationTypeOption
  );

  const navigate = useNavigate();
  const location = useLocation();
  const singleViewId = location?.state?._id || null;

  const { selectedChartOfAccount, singleViewData } = useSelector(
    (state) => state.payrollSlice
  );

  // category groupings for easier conditional rendering
  const CATEGORIES = {
    FIXED_CATEGORIES: [
      "LEAVE ENCASHMENT",
      "REIMBURSEMENTS",
      "BENEFITS",
      "BONUS",
    ],
    CALCULATED_CATEGORIES: ["ALLOWANCE", "BASIC"],
    AMOUNT_CATEGORIES: ["COMMISSION", "DEDUCTION"],
  };

  const [formData, setFormData] = useState({
    category: null,
    name: null,
    calculationType: null,
    value: null,
    account: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isEdit, setIsEdit] = useState(false);

  // Simplified handleChange
  const handleChange = (field) => (event, newValue) => {
    const value = newValue ?? event?.target?.value ?? null;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // validation
  const validateForm = () => {
    const newErrors = {};
    const { category, name, calculationType, value, account } = formData;

    if (!category) newErrors.category = "Required";
    if (!name) newErrors.name = "Required";

    // Validate value field
    if (
      CATEGORIES.FIXED_CATEGORIES.includes(category) ||
      CATEGORIES.AMOUNT_CATEGORIES.includes(category) ||
      (CATEGORIES.CALCULATED_CATEGORIES.includes(category) && calculationType)
    ) {
      if (!value) newErrors.value = "Required";
    }

    // Validate account field
    if (category && category !== "COMMISSION") {
      if (!account) newErrors.account = "Required";
    }

    // Validate calculation type for specific categories
    if (
      CATEGORIES.CALCULATED_CATEGORIES.includes(category) &&
      !calculationType
    ) {
      newErrors.calculationType = "Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setSaveBtnLoading(true);
      const payload = {
        category: formData.category,
        name: formData.name,
        calculationType: ["ALLOWANCE", "BASIC"].includes(formData.category)
          ? formData.calculationType?.value
          : null,
        amountOrPerc: formData.value,
        expAccnt:
          formData.category !== "COMMISSION" ? formData.account?._id : null,
      };

      if (singleViewId) {
        updatePayHeadApi({
          body: { ...payload, id: singleViewId },
          setSuccess,
          setSnackbar,
          setSaveBtnLoading,
          setIsEdit,
        });
      } else {
        addPayHeadApi({
          body: payload,
          setSuccess,
          setSnackbar,
          setSaveBtnLoading,
          handleBack,
        });
      }
    }
  };

  // Determine which fields to show based on category
  const shouldShowCalculationType = CATEGORIES.CALCULATED_CATEGORIES.includes(
    formData.category
  );
  const shouldShowValue =
    formData.category &&
    (CATEGORIES.FIXED_CATEGORIES.includes(formData.category) ||
      CATEGORIES.AMOUNT_CATEGORIES.includes(formData.category) ||
      (shouldShowCalculationType && formData.calculationType));
  const shouldShowAccount =
    formData.category && formData.category !== "COMMISSION";

  const getValueFieldLabel = () => {
    if (CATEGORIES.FIXED_CATEGORIES.includes(formData.category))
      return "Fixed Amount";
    if (CATEGORIES.AMOUNT_CATEGORIES.includes(formData.category))
      return "Amount";
    if (formData.calculationType?.value === "FIXED") return "Fixed Amount";
    if (formData.calculationType?.value === "CTCPERCENT")
      return "Percentage of CTC";
    if (formData.calculationType?.value === "BASICPERCENT")
      return "Percentage of Basic";
    return "Value";
  };

  const handleEdit = () => {
    setIsEdit(!isEdit);
  };

  useEffect(() => {
    payHeadInitialApi({ setSuccess, setSnackbar, setIsLoading, setErrorMsg });
    if (singleViewData) {
      setIsEdit(true);
      setFormData({
        category: singleViewData.category,
        name: singleViewData.name,
        calculationType:
          calculationTypeOption.find(
            (res) => res.value === singleViewData.calculationType
          ) || null,
        value: singleViewData.amountOrPerc,
        account:
          selectedChartOfAccount?.find(
            (r) => r._id === singleViewData?.expAccnt
          ) || null,
      });
    } else {
      setIsEdit(false);
      if (singleViewId) {
        singlePayHeadApi({ body: { id: singleViewId } });
      }
    }
  }, [singleViewData, singleViewId]);

  const handleRetry = async () => {
    setIsLoading(true);
    setErrorMsg({ message: "" });
    await payHeadInitialApi({
      setSuccess,
      setSnackbar,
      setIsLoading,
      setErrorMsg,
    });
  };

  const handleBack = () => {
    window.history.back();
  };

  useEffect(() => {
    if (formData.category === "BASIC") {
      setCalculationTypeOptions(
        calculationTypeOption.filter((obj) => obj.value !== "BASICPERCENT")
      );
    } else if (formData.category === "ALLOWANCE") {
      setCalculationTypeOptions(
        calculationTypeOption.filter((obj) => obj.value !== "CTCPERCENT")
      );
    }
  }, [formData.category]);

  return (
    <Pagelayout
      pageName="Create Pay Head"
      headerLayout={2}
      handleBack={() =>
        navigate("/userdashboard/payroll/configuration/PayrollPayHead/")
      }
      handleSave={handleSave}
      newData={!isEdit}
      success={success}
      saveBtnLoading={saveBtnLoading}
      setSnackbar={setSnackbar}
      snackbar={snackbar}
      handleEdit={handleEdit}
      actionBtn={errorMsg.message ? false : true}
      isLoading={isLoading}
      status={singleViewId?"done":"new"}
    >
      {isLoading ? (
        <FormSkeleton />
      ) : errorMsg.message ? (
        <ErrorDisplay
          message={errorMsg.message}
          onRetry={handleRetry}
          onBack={handleBack}
        />
      ) : (
        <Grid container spacing={3}>
          {/* Category Selection */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              disabled={isEdit}
              size="small"
              options={Object.values(CATEGORIES).flat()}
              value={formData.category}
              onChange={handleChange("category")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  label="Category"
                  error={!!errors.category}
                  helperText={errors.category}
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Name Field */}
          <Grid item xs={12} md={4}>
            <TextField
              disabled={isEdit}
              size="small"
              label="Name"
              value={formData.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Calculation Type */}
          {shouldShowCalculationType && (
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={isEdit}
                size="small"
                options={calculationTypeOptions}
                value={formData.calculationType}
                getOptionLabel={(option) => option?.key || ""}
                onChange={handleChange("calculationType")}
                isOptionEqualToValue={(option, value) =>
                  option.key === value?.key
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Calculation Type"
                    error={!!errors.calculationType}
                    helperText={errors.calculationType}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Value Field (Amount/Percentage) */}
          {shouldShowValue && (
            <Grid item xs={12} md={4}>
              <TextField
                disabled={isEdit}
                size="small"
                label={getValueFieldLabel()}
                value={formData.value}
                onChange={handleChange("value")}
                error={!!errors.value}
                helperText={errors.value}
                fullWidth
                type="number"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          {/* Account Field */}
          {shouldShowAccount && (
            <Grid item xs={12} md={4}>
              <Autocomplete
                disabled={isEdit}
                size="small"
                options={selectedChartOfAccount || []}
                getOptionLabel={(option) => option.accountName || ""}
                getOptionKey={(option) => option._id || option.accountName}
                isOptionEqualToValue={(option, value) =>
                  option._id === value?._id
                }
                value={formData.account}
                onChange={handleChange("account")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account"
                    error={!!errors.account}
                    helperText={errors.account}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Pagelayout>
  );
};

export default CreatePayHead;

const FormSkeleton = () => {
  return (
    <Grid container spacing={3}>
      {/* Category Selection */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>

      {/* Name Field */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>

      {/* Calculation Type */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>

      {/* Value Field */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>

      {/* Account Field */}
      <Grid item xs={12} md={4}>
        <Skeleton variant="rectangular" height={56} />
      </Grid>
    </Grid>
  );
};
